import { Models } from "@hex-insights/app-modules";
import {
	dateDiff,
	dateTrunc,
	joinStrings,
	NullablePick,
	stringToLocalDate,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { applicationDetails } from "../../../settings";
import { Person, PersonNameFragment, PersonNameOrder, PersonPublicNameFormat } from "../../../Utilities";
import * as EmployeeFormat from "../Employee";
import * as Edges from "./edges";
import * as Fields from "./fields";

export { Fields, Edges };

export type NameOptions = {
	ignoreUser?: boolean;
	ignorePublicName?: boolean;
};

/**
 * Formats the full name of the given instance.
 */
export function name(
	instance: PersonNameFragment & { user?: NullablePick<Person["user"], "isCurrent"> },
	{ ignoreUser, ignorePublicName }: NameOptions = {},
) {
	if (!ignoreUser && instance.user?.isCurrent) {
		return "You";
	}

	if (!ignorePublicName && applicationDetails.portal === "parent-portal") {
		return Fields.publicName(instance.publicName);
	}

	return Fields.name(instance.name);
}

export function nameFromFields(
	instance: Pick<Person, "givenName" | "familyName" | "nickname"> & { nameOrder: Person["nameOrder"] | null },
) {
	let givenNames = instance.givenName;
	if (instance.nickname !== "") {
		givenNames += ` (${instance.nickname})`;
	}
	if (instance.nameOrder === PersonNameOrder.FamilyGiven) {
		return instance.familyName + " " + givenNames;
	}
	return givenNames + " " + instance.familyName;
}

export function publicNameFromFields(
	instance: Pick<Person, "givenName" | "familyName"> & {
		nameOrder: Person["nameOrder"] | null;
		publicNameFormat: Person["publicNameFormat"] | null;
	},
) {
	let givenDisplay = "";
	let familyDisplay = "";
	switch (instance.publicNameFormat) {
		case null:
		case PersonPublicNameFormat.FullName:
			givenDisplay = instance.givenName;
			familyDisplay = instance.familyName;
			break;
		case PersonPublicNameFormat.GivenNameFamilyInitial:
			givenDisplay = instance.givenName;
			familyDisplay = instance.familyName.charAt(0);
			break;
		case PersonPublicNameFormat.GivenInitialFamilyName:
			givenDisplay = instance.givenName.charAt(0);
			familyDisplay = instance.familyName;
			break;
		case PersonPublicNameFormat.GivenNameOnly:
			givenDisplay = instance.givenName;
			break;
		case PersonPublicNameFormat.FamilyNameOnly:
			familyDisplay = instance.familyName;
			break;
		case PersonPublicNameFormat.Hidden:
			return "Anonymous";
	}

	if (instance.nameOrder === PersonNameOrder.FamilyGiven) {
		return joinStrings([familyDisplay, givenDisplay], ", ");
	}

	return joinStrings([givenDisplay, familyDisplay], " ");
}

/**
 * Formats the role of the given instance (depending on what type of person it is).
 */
export function role(instance: {
	employee: NullablePick<Person["employee"], "jobTitle">;
	guardian: NullablePick<Person["guardian"], "id">;
}) {
	if (instance.employee) {
		return EmployeeFormat.Fields.jobTitle(instance.employee.jobTitle);
	}
	if (instance.guardian) {
		return "Parent/Guardian";
	}
	return "Person";
}

/**
 * Formats the age the person given their date of birth
 */
export function age(dob: Person["dateOfBirth"] | null) {
	if (dob === null) {
		return Models.toString(dob);
	}
	const years = dateDiff(dateTrunc(new Date(), "day"), stringToLocalDate(dob, "day"), "year");
	return `${toGrammaticalNumber("year", years, true)} old`;
}
